import React, { ReactNode, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useLocation, useSearchParams, Outlet } from 'react-router-dom';
import { ProfileType } from '@ixicare/resources';
import { useStores } from '~store/utils';
import { buildMenu, pruneMenu } from '~/config/menu';
import AuthLayout from '~/components/AuthLayout';
import DashboardWrapper from '~components/DashboardWrapper/DashboardWrapper';
import ErrorPage from '~/pages/ErrorPage';
import { t } from '~/i18n';

interface AuthWrapperContainerProps {
  isAuthScreen: boolean;
  authScreen?: {
    profileTypes?: ProfileType[];
  };
  children?: ReactNode;
}

const AuthWrapperContainer: React.FC<AuthWrapperContainerProps> = ({ children, isAuthScreen, authScreen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { auth, permissions } = useStores();

  // Force observer to track auth.user by accessing it in render
  const { loading, isLoggedIn, isInitialized, userType } = auth;

  useEffect(() => {
    auth.init();
  }, []);

  useEffect(() => {
    if (!loading && !isLoggedIn && isAuthScreen) {
      navigate(`/login?after_login=${encodeURIComponent(location.pathname)}`);
      return;
    }

    const isOnLoginPage = location.pathname === '/login' || location.pathname === '/login/';
    const afterLoginPath = searchParams.get('after_login');

    if (isLoggedIn && isOnLoginPage) {
      if (afterLoginPath && afterLoginPath !== '/') {
        navigate(decodeURIComponent(afterLoginPath));
      } else {
        navigate('/dashboard');
      }
    }
  }, [loading, isLoggedIn, isAuthScreen, location.pathname, navigate, searchParams, auth, userType]);

  // Wait for auth initialization
  if (!isInitialized) {
    return null;
  }

  // Redirect if not logged in (handled by useEffect)
  if (!isLoggedIn && isAuthScreen) {
    return null;
  }

  // Check profile type permissions
  if (authScreen && authScreen.profileTypes && userType && !authScreen.profileTypes.includes(userType)) {
    return <ErrorPage statusCode={404} />;
  }

  // Logged in users get dashboard wrapper
  if (isLoggedIn && isAuthScreen) {
    const menuItems = pruneMenu(
      buildMenu(permissions.has('can_view_site')).filter((row) => {
        if (!('route' in row)) return true;
        if (row.profileType && userType && !row.profileType.includes(userType)) return false;
        if (row.permissions && !row.permissions.every((p) => permissions.has(p))) return false;
        return true;
      })
    );
    return <DashboardWrapper routes={menuItems}>{children || <Outlet />}</DashboardWrapper>;
  }

  // Guest pages (login, etc.) get auth layout
  if (!isAuthScreen) {
    return (
      <AuthLayout
        quote={{
          text: t('auth.quote.text'),
        }}
      >
        {children || <Outlet />}
      </AuthLayout>
    );
  }

  return <>{children || <Outlet />}</>;
};

export default observer(AuthWrapperContainer);
